import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import Component from '../../components/Component';
import {
  getFanEnergyApiPayload,
  getFanLedgerTransactions,
  getFanLedgerUser,
  getFanLedgerUsers,
  isFanEnergyApiSuccess,
} from '../../services/fanEnergyApi';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';

const USER_PAGE_SIZE = 20;
const TRANSACTION_PAGE_SIZE = 50;

const formatNumber = (value) =>
  value == null || Number.isNaN(Number(value)) ? '—' : Number(value).toLocaleString('en-IN');
const formatFe = (value) => (value == null ? '—' : `${formatNumber(value)} FE`);
const formatDate = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString();
};
const formatSeason = (season) => {
  if (!season) return null;
  const label = season.seasonNumber != null ? `Season #${season.seasonNumber}` : 'Current season';
  return season.startDate && season.endDate ? `${label} · ${season.startDate} → ${season.endDate}` : label;
};

function SummaryCard({ label, value, detail, color }) {
  return (
    <Paper variant="outlined" sx={{ p: 2, height: '100%', borderTop: 3, borderColor: color }}>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="h5" fontWeight={700} sx={{ mt: 0.5 }}>
        {value}
      </Typography>
      {detail && (
        <Typography variant="caption" color="text.secondary">
          {detail}
        </Typography>
      )}
    </Paper>
  );
}

function FanLedgerUsers() {
  const [users, setUsers] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [season, setSeason] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [qInput, setQInput] = useState('');
  const [minFanEnergyInput, setMinFanEnergyInput] = useState('');
  const [filters, setFilters] = useState({
    q: '',
    minFanEnergy: '',
    sortBy: 'fan_energy',
    sortOrder: 'desc',
    offset: 0,
  });

  const loadUsers = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const result = await getFanLedgerUsers({ ...filters, limit: USER_PAGE_SIZE });
      if (!isFanEnergyApiSuccess(result)) {
        setError(result?.message || 'Failed to load fan ledger users.');
        setUsers([]);
        return;
      }
      const data = getFanEnergyApiPayload(result);
      setUsers(data?.users || []);
      setPagination(data?.pagination || null);
      setSeason(data?.season || null);
    } catch (fetchError) {
      setError(fetchError.message || 'Failed to load fan ledger users.');
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const submitFilters = () =>
    setFilters((current) => ({
      ...current,
      q: qInput,
      minFanEnergy: minFanEnergyInput,
      offset: 0,
    }));
  const updateSortBy = (sortBy) =>
    setFilters((current) => ({
      ...current,
      sortBy,
      sortOrder: sortBy === 'rank' ? 'asc' : sortBy === 'season_earned' ? 'desc' : current.sortOrder,
      offset: 0,
    }));
  const rankFirst = filters.sortBy === 'rank';

  return (
    <LedgerShell
      title="Fan Ledger"
      subtitle="Find a user and inspect their complete Fan Energy audit trail."
      extraActions={
        <Button component={Link} to="/admin/fan-energy/leaderboard/season" size="small" variant="outlined">
          FE Leaderboard
        </Button>
      }
    >
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', mb: 2 }}>
        <TextField
          size="small"
          label="Search user"
          placeholder="ID, username, name, email, phone, referral code"
          value={qInput}
          onChange={(event) => setQInput(event.target.value)}
          onKeyDown={(event) => event.key === 'Enter' && submitFilters()}
          sx={{ minWidth: 300, flex: '1 1 300px' }}
        />
        <TextField
          size="small"
          label="Min balance"
          type="number"
          value={minFanEnergyInput}
          onChange={(event) => setMinFanEnergyInput(event.target.value)}
          inputProps={{ min: 0 }}
          sx={{ width: 150 }}
        />
        <TextField
          select
          size="small"
          label="Sort by"
          value={filters.sortBy}
          onChange={(event) => updateSortBy(event.target.value)}
          sx={{ width: 150 }}
        >
          <MenuItem value="fan_energy">Balance</MenuItem>
          <MenuItem value="rank">Rank (best first)</MenuItem>
          <MenuItem value="season_earned">Season earned</MenuItem>
          <MenuItem value="earned">Lifetime earned</MenuItem>
          <MenuItem value="created_at">Joined</MenuItem>
        </TextField>
        <TextField
          select
          size="small"
          label="Order"
          value={filters.sortOrder}
          onChange={(event) => setFilters((current) => ({ ...current, sortOrder: event.target.value, offset: 0 }))}
          sx={{ width: 120 }}
        >
          <MenuItem value="desc">Descending</MenuItem>
          <MenuItem value="asc">Ascending</MenuItem>
        </TextField>
        <Button variant="contained" startIcon={<SearchIcon />} onClick={submitFilters} disabled={loading}>
          Search
        </Button>
        <Button variant="text" startIcon={<RefreshIcon />} onClick={loadUsers} disabled={loading}>
          Refresh
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      ) : (
        <>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {pagination ? `${formatNumber(pagination.total)} user${pagination.total === 1 ? '' : 's'} found` : 'Users'}
          </Typography>
          {formatSeason(season) && (
            <Chip label={formatSeason(season)} color="primary" variant="outlined" sx={{ mb: 1.5 }} />
          )}
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  {rankFirst && <TableCell>Rank</TableCell>}
                  <TableCell>User</TableCell><TableCell>Contact</TableCell><TableCell align="right">Balance</TableCell>
                  <TableCell align="right">Season earned</TableCell><TableCell align="right">Lifetime earned</TableCell>
                  <TableCell align="right">Lifetime spent</TableCell>{!rankFirst && <TableCell>Rank</TableCell>}
                  <TableCell>Joined</TableCell><TableCell align="right">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.length === 0 ? (
                  <TableRow><TableCell colSpan={9} align="center">No users found.</TableCell></TableRow>
                ) : users.map((user) => (
                  <TableRow key={user.userId} hover>
                    {rankFirst && (
                      <TableCell>
                        {user.currentRank != null ? `#${user.currentRank}` : '—'}
                        {user.bestRank != null && <Typography variant="caption" color="text.secondary" display="block">Best #{user.bestRank}</Typography>}
                      </TableCell>
                    )}
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>{user.name || user.username || `User ${user.userId}`}</Typography>
                      <Typography variant="caption" color="text.secondary">@{user.username || '—'} · ID {user.userId}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{user.email || '—'}</Typography>
                      <Typography variant="caption" color="text.secondary">{user.phone || '—'}</Typography>
                    </TableCell>
                    <TableCell align="right"><Typography fontWeight={700}>{formatFe(user.fanEnergy)}</Typography></TableCell>
                    <TableCell align="right" sx={{ color: 'success.main', fontWeight: 700 }}>{formatFe(user.seasonEarned)}</TableCell>
                    <TableCell align="right">{formatFe(user.earned)}</TableCell>
                    <TableCell align="right">{formatFe(user.used)}</TableCell>
                    {!rankFirst && <TableCell>
                      {user.currentRank != null ? `#${user.currentRank}` : '—'}
                      {user.bestRank != null && <Typography variant="caption" color="text.secondary" display="block">Best #{user.bestRank}</Typography>}
                    </TableCell>}
                    <TableCell>{formatDate(user.createdAt)}</TableCell>
                    <TableCell align="right">
                      <Button component={Link} to={`/admin/fan-ledger/${user.userId}`} size="small" endIcon={<OpenInNewIcon />}>Open ledger</Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Button disabled={loading || filters.offset === 0} onClick={() => setFilters((current) => ({ ...current, offset: Math.max(0, current.offset - USER_PAGE_SIZE) }))}>Previous</Button>
            <Button disabled={loading || !pagination?.hasMore} onClick={() => setFilters((current) => ({ ...current, offset: current.offset + USER_PAGE_SIZE }))}>Next</Button>
            <Typography variant="body2" color="text.secondary">Offset {filters.offset}</Typography>
          </Box>
        </>
      )}
    </LedgerShell>
  );
}

function FanLedgerDetail() {
  const { userId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [detail, setDetail] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(true);
  const [loadingTransactions, setLoadingTransactions] = useState(true);
  const [error, setError] = useState('');
  const [txnFilters, setTxnFilters] = useState({
    direction: searchParams.get('direction') || '',
    type: searchParams.get('type') || '',
    from: searchParams.get('from') || '',
    to: searchParams.get('to') || '',
    q: searchParams.get('q') || '',
  });
  const [appliedFilters, setAppliedFilters] = useState(txnFilters);

  const loadDetail = useCallback(async () => {
    setLoadingDetail(true);
    setError('');
    try {
      const result = await getFanLedgerUser(userId);
      if (!isFanEnergyApiSuccess(result)) {
        setError(result?.message || 'Failed to load this user.');
        setDetail(null);
        return;
      }
      setDetail(getFanEnergyApiPayload(result));
    } catch (fetchError) {
      setError(fetchError.message || 'Failed to load this user.');
      setDetail(null);
    } finally {
      setLoadingDetail(false);
    }
  }, [userId]);

  const loadTransactions = useCallback(async (offset = 0, append = false) => {
    setLoadingTransactions(true);
    try {
      const result = await getFanLedgerTransactions(userId, { ...appliedFilters, limit: TRANSACTION_PAGE_SIZE, offset });
      if (!isFanEnergyApiSuccess(result)) {
        setError(result?.message || 'Failed to load ledger transactions.');
        if (!append) setTransactions([]);
        return;
      }
      const data = getFanEnergyApiPayload(result);
      setTransactions((current) => (append ? [...current, ...(data?.transactions || [])] : (data?.transactions || [])));
      setPagination(data?.pagination || null);
    } catch (fetchError) {
      setError(fetchError.message || 'Failed to load ledger transactions.');
    } finally {
      setLoadingTransactions(false);
    }
  }, [appliedFilters, userId]);

  useEffect(() => { loadDetail(); }, [loadDetail]);
  useEffect(() => { loadTransactions(); }, [loadTransactions]);

  const applyTransactionFilters = () => {
    const params = {};
    Object.entries(txnFilters).forEach(([key, value]) => { if (value) params[key] = value; });
    setSearchParams(params);
    setAppliedFilters(txnFilters);
  };

  const user = detail?.user;
  const breakdown = detail?.breakdownByType || [];
  const season = detail?.season;
  return (
    <LedgerShell
      title={user ? `Fan Ledger — ${user.name || user.username || `User ${user.userId}`}` : 'Fan Ledger'}
      subtitle={user ? `User ID ${user.userId}${user.username ? ` · @${user.username}` : ''}${formatSeason(season) ? ` · ${formatSeason(season)}` : ''}` : 'Loading user details…'}
      backTo="/admin/fan-ledger"
      backLabel="All users"
    >
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loadingDetail ? <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box> : !user ? null : (
        <>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}><SummaryCard label="Current balance" value={formatFe(user.fanEnergy)} color="success.main" /></Grid>
            <Grid item xs={12} sm={6} md={3}><SummaryCard label="Season earned" value={formatFe(user.seasonEarned)} detail={formatSeason(season) || undefined} color="secondary.main" /></Grid>
            <Grid item xs={12} sm={6} md={3}><SummaryCard label="Lifetime earned" value={formatFe(user.earned)} color="primary.main" /></Grid>
            <Grid item xs={12} sm={6} md={3}><SummaryCard label="Lifetime spent" value={formatFe(user.used)} color="error.main" /></Grid>
            <Grid item xs={12} sm={6} md={3}><SummaryCard label="Current / best rank" value={user.currentRank != null ? `#${user.currentRank}` : '—'} detail={user.bestRank != null ? `Best rank #${user.bestRank}` : undefined} color="warning.main" /></Grid>
          </Grid>
          <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>Profile</Typography>
            <Grid container spacing={1}>
              <Grid item xs={12} sm={6}><Typography variant="body2"><strong>Email:</strong> {user.email || '—'}</Typography></Grid>
              <Grid item xs={12} sm={6}><Typography variant="body2"><strong>Phone:</strong> {user.phone || '—'}</Typography></Grid>
              <Grid item xs={12} sm={6}><Typography variant="body2"><strong>Referral code:</strong> {user.referralCode || '—'}</Typography></Grid>
              <Grid item xs={12} sm={6}><Typography variant="body2"><strong>Joined:</strong> {formatDate(user.createdAt)}</Typography></Grid>
            </Grid>
          </Paper>
          <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>Earned and spent by type</Typography>
            <TableContainer>
              <Table size="small">
                <TableHead><TableRow><TableCell>Type</TableCell><TableCell>Category</TableCell><TableCell align="right">Earned</TableCell><TableCell align="right">Spent</TableCell><TableCell align="right">Transactions</TableCell></TableRow></TableHead>
                <TableBody>
                  {breakdown.length === 0 ? <TableRow><TableCell colSpan={5} align="center">No transaction types found.</TableCell></TableRow> : breakdown.map((item) => (
                    <TableRow key={item.transactionType} hover sx={{ cursor: 'pointer' }} onClick={() => {
                      const next = { ...txnFilters, type: item.transactionType };
                      setTxnFilters(next); setAppliedFilters(next); setSearchParams({ type: item.transactionType });
                    }}>
                      <TableCell><Typography variant="body2" fontWeight={600}>{item.transactionName}</Typography><Typography variant="caption" color="text.secondary">{item.transactionType}</Typography></TableCell>
                      <TableCell><Chip size="small" label={item.category || '—'} /></TableCell>
                      <TableCell align="right" sx={{ color: 'success.main' }}>{formatFe(item.earned)}</TableCell>
                      <TableCell align="right" sx={{ color: 'error.main' }}>{formatFe(item.used)}</TableCell>
                      <TableCell align="right">{formatNumber(item.txnCount)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </>
      )}

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="subtitle1" fontWeight={600} gutterBottom>Immutable transaction ledger</Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
          <TextField select size="small" label="Direction" value={txnFilters.direction} onChange={(event) => setTxnFilters((current) => ({ ...current, direction: event.target.value }))} sx={{ minWidth: 135 }}>
            <MenuItem value="">All</MenuItem><MenuItem value="credit">Credits</MenuItem><MenuItem value="debit">Debits</MenuItem>
          </TextField>
          <TextField select size="small" label="Type" value={txnFilters.type} onChange={(event) => setTxnFilters((current) => ({ ...current, type: event.target.value }))} sx={{ minWidth: 220 }}>
            <MenuItem value="">All types</MenuItem>
            {breakdown.map((item) => <MenuItem key={item.transactionType} value={item.transactionType}>{item.transactionName} ({item.transactionType})</MenuItem>)}
          </TextField>
          <TextField size="small" label="From" type="date" value={txnFilters.from} InputLabelProps={{ shrink: true }} onChange={(event) => setTxnFilters((current) => ({ ...current, from: event.target.value }))} />
          <TextField size="small" label="To" type="date" value={txnFilters.to} InputLabelProps={{ shrink: true }} onChange={(event) => setTxnFilters((current) => ({ ...current, to: event.target.value }))} />
          <TextField size="small" label="Search ledger" value={txnFilters.q} onChange={(event) => setTxnFilters((current) => ({ ...current, q: event.target.value }))} onKeyDown={(event) => event.key === 'Enter' && applyTransactionFilters()} sx={{ minWidth: 180 }} />
          <Button variant="contained" onClick={applyTransactionFilters} disabled={loadingTransactions}>Apply filters</Button>
        </Box>
        {loadingTransactions && transactions.length === 0 ? <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box> : (
          <>
            <TableContainer>
              <Table size="small">
                <TableHead><TableRow><TableCell>When</TableCell><TableCell>Name / type</TableCell><TableCell>Source</TableCell><TableCell align="right">Change</TableCell><TableCell align="right">Balance after</TableCell><TableCell>Description</TableCell><TableCell>ID</TableCell></TableRow></TableHead>
                <TableBody>
                  {transactions.length === 0 ? <TableRow><TableCell colSpan={7} align="center">No transactions match these filters.</TableCell></TableRow> : transactions.map((transaction) => (
                    <TableRow key={transaction.id} hover>
                      <TableCell>{formatDate(transaction.createdAt)}</TableCell>
                      <TableCell><Typography variant="body2" fontWeight={600}>{transaction.transactionName}</Typography><Typography variant="caption" color="text.secondary">{transaction.transactionType}</Typography></TableCell>
                      <TableCell>{transaction.transactionSource || '—'}</TableCell>
                      <TableCell align="right" sx={{ color: transaction.direction === 'credit' ? 'success.main' : transaction.direction === 'debit' ? 'error.main' : 'text.primary', fontWeight: 700 }}>{Number(transaction.energyChange) > 0 ? '+' : ''}{formatNumber(transaction.energyChange)}</TableCell>
                      <TableCell align="right">{formatFe(transaction.balanceAfterChange)}</TableCell>
                      <TableCell sx={{ maxWidth: 220, wordBreak: 'break-word' }}>{transaction.description || '—'}</TableCell>
                      <TableCell><Typography variant="caption">#{transaction.id}</Typography><Typography variant="caption" color="text.secondary" display="block">{transaction.transactionId}</Typography></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" color="text.secondary">{pagination ? `${formatNumber(pagination.total)} transaction${pagination.total === 1 ? '' : 's'}` : ''}</Typography>
              <Button disabled={!pagination?.hasMore || loadingTransactions} onClick={() => loadTransactions(transactions.length, true)}>{loadingTransactions ? 'Loading…' : 'Load more'}</Button>
            </Box>
          </>
        )}
      </Paper>
    </LedgerShell>
  );
}

function LedgerShell({ title, subtitle, backTo = '/admin/fan-energy', backLabel = 'Fan Energy dashboard', extraActions, children }) {
  return (
    <Component>
      <div className="page-body"><div className="container-fluid"><div className="page-header"><div className="row"><div className="col-sm-12">
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Button component={Link} to={backTo} startIcon={<ArrowBackIcon />} variant="outlined" size="small">{backLabel}</Button>
          <Typography variant="h5" component="h1">{title}</Typography>
          {extraActions}
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>{subtitle}</Typography>
      </div></div></div></div>
      <div className="container-fluid"><Card><CardContent>{children}</CardContent></Card></div>
      </div>
    </Component>
  );
}

function FanLedger() {
  const { userId } = useParams();
  return userId ? <FanLedgerDetail /> : <FanLedgerUsers />;
}

export default FanLedger;
